/**
 * Vehicle resolver — the shared brain behind the vehicle finder (RAZ-327), the header
 * chip and panel (RAZ-337), the PDP fitment card (RAZ-338) and the migration's
 * verify step.
 *
 * Every function here is pure: no network, no DOM, no storage, no Alpine. The only
 * platform API used is URLSearchParams, so the destination query string is encoded
 * exactly the way the theme's search store writes and reads it.
 *
 * Every export also has to survive a cleared Alpine `$persist` store, which hands
 * back an explicit `null` rather than `undefined`, so nothing here relies on a
 * default parameter to fill a missing object in.
 *
 * The raw payload is the columnar document emitted by sections/rb-vehicle-data.liquid.
 * Call indexData() once, then pass the returned index to everything else.
 */

/** Product metafield filter the destination query narrows on. */
export const FILTER_KEY = 'filter.p.m.custom.vehicle_variants';

/** Separator packing body type, variant and qualifier into one <option value>. */
export const VARIANT_VALUE_SEPARATOR = '~|~';

/**
 * Raw export "Body Type" spellings that name a catalogue body type under another
 * form. scripts/ymm/lib/maps.mjs imports this table and builds its own BODY_NAMES
 * entries from it, so the importer and the storefront can never disagree about
 * what a body cell means. Keys are the cell as the export spells it; matching is
 * case-insensitive.
 */
export const BODY_TYPE_ALIASES = {
  'DUAL/DOUBLE CAB': 'Dual Cab',
};

/** BODY_TYPE_ALIASES keyed by comparison fold, built once. */
const BODY_ALIAS_BY_FOLD = new Map(
  Object.entries(BODY_TYPE_ALIASES).map(([raw, body]) => [fold(raw), body]),
);

/**
 * Levels the finder can ask about, in cascade order.
 *
 *  - `year`  the shopper's build year. Customers name their vehicle by year far more
 *            readily than by generation, so it is asked straight after the model, and
 *            it narrows everything below it.
 *  - `trim`  generation, body type, variant and qualifier in one select. With the year
 *            known the generation is usually settled already, so it appears only as the
 *            select's group headings in the years a model straddles two of them.
 *  - `band`  which side of a mid-year facelift, for the handful of vehicles whose
 *            destination changes part-way through a year. Nobody else ever sees it.
 */
export const LEVELS = ['make', 'model', 'year', 'trim', 'band'];

/**
 * nextStep()'s answer when the selection matches no fitment at all, which is a
 * different outcome from "nothing left to ask" and must never be mistaken for it.
 */
export const IMPOSSIBLE = 'impossible';

/** Option label for a fitment that carries neither a variant nor a qualifier. */
const STANDARD_LABEL = 'Standard';

/** Columns sections/rb-vehicle-data.liquid emits for each fitment, in order. */
const FITMENT_COLUMNS = [
  'id',
  'model',
  'series',
  'body_type',
  'variant',
  'qualifier',
  'year_from',
  'year_to',
  'year_label',
  'collection',
  'definitive',
  'filters',
  'note',
];

/**
 * Cascade level to the word the shopper-facing copy strings use for it.
 * `series`, `variant` and `years` are the pre-year-first levels, kept so a vehicle saved
 * under the old cascade still reads sensibly.
 */
const NEXT_STEP_WORDS = {
  year: 'year',
  trim: 'variant',
  band: 'build date',
  series: 'generation',
  variant: 'variant',
  years: 'year',
};

const EN_DASH = '–';
const MIDDLE_DOT = '·';
const OPEN_ENDED_LABEL = 'now';

/* -------------------------------------------------------------------------- */
/* Small pure helpers                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Trim, collapse inner whitespace and turn blanks into null.
 * @param {*} value
 * @returns {string|null}
 */
function text(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim().replace(/\s+/g, ' ');
  return trimmed === '' ? null : trimmed;
}

/**
 * Case-insensitive comparison key, or null for blanks.
 * @param {*} value
 * @returns {string|null}
 */
function fold(value) {
  const trimmed = text(value);
  return trimmed === null ? null : trimmed.toLowerCase();
}

/**
 * Punctuation-insensitive comparison key, so "D-MAX" and "D MAX" match.
 * @param {*} value
 * @returns {string|null}
 */
function slugFold(value) {
  const folded = fold(value);
  if (folded === null) return null;
  const stripped = folded.replace(/[^a-z0-9]+/g, '');
  return stripped === '' ? null : stripped;
}

/**
 * Coerce a year cell to a finite number, or null.
 * @param {*} value
 * @returns {number|null}
 */
function toYear(value) {
  if (value === null || value === undefined || value === '') return null;
  const year = Number(value);
  return Number.isFinite(year) ? year : null;
}

/**
 * Always hand back an array of rows, whatever the payload gave us.
 * @param {*} value
 * @returns {Array}
 */
function toRows(value) {
  return Array.isArray(value) ? value.filter(Array.isArray) : [];
}

/**
 * Always hand back an array of objects, dropping blanks.
 * @param {*} value
 * @returns {Array}
 */
function toList(value) {
  return Array.isArray(value) ? value.filter((entry) => entry !== null && entry !== undefined) : [];
}

/**
 * Join label parts with a single space, dropping blanks.
 * @param {Array<string|null>} parts
 * @returns {string}
 */
function joinWords(parts) {
  return parts.map((part) => text(part)).filter(Boolean).join(' ');
}

/**
 * Push a handle onto a list once.
 * @param {Array<string>} list
 * @param {*} handle
 */
function pushUnique(list, handle) {
  const trimmed = text(handle);
  if (trimmed !== null && !list.includes(trimmed)) list.push(trimmed);
}

/**
 * Human year range, e.g. "2015 – now" or "2009 – 2021".
 * @param {number|null} from
 * @param {number|null} to
 * @returns {string}
 */
export function yearRangeLabel(from, to) {
  if (from === null || from === undefined) return to ? String(to) : '';
  return `${from} ${EN_DASH} ${to === null || to === undefined ? OPEN_ENDED_LABEL : to}`;
}

/**
 * Does a fitment's year range cover this year?
 * @param {Object|null} fitment
 * @param {number} year
 * @returns {boolean}
 */
export function coversYear(fitment, year) {
  const known = toYear(year);
  if (known === null) return true;
  const entry = fitment ?? {};
  const from = toYear(entry.yearFrom);
  const to = toYear(entry.yearTo);
  const startsBefore = from === null || from <= known;
  const endsAfter = to === null || known <= to;
  return startsBefore && endsAfter;
}

/**
 * The calendar year an open-ended range runs up to.
 *
 * A blank `year_to` means "still on sale", so the Year select has to grow on
 * 1 January by itself. Nothing stores the current year: it is read from the clock at
 * the moment the options are built, injectable so the tests stay deterministic.
 *
 * @param {number} [now] epoch milliseconds
 * @returns {number}
 */
function currentYear(now = Date.now()) {
  return new Date(now).getFullYear();
}

/* -------------------------------------------------------------------------- */
/* 1. indexData                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Fail loudly when the fitment rows are not the shape this module reads.
 *
 * The columns are read positionally, so a document with one column missing would
 * otherwise shift every field past it and silently drop the filters, the note and
 * the definitive flag rather than erroring.
 *
 * @param {Array<Array>} rows
 */
function assertFitmentShape(rows) {
  const wrong = rows.findIndex((row) => row.length !== FITMENT_COLUMNS.length);
  if (wrong === -1) return;
  throw new Error(
    `rb-vehicle-data fitment row ${wrong} has ${rows[wrong].length} columns, expected `
      + `${FITMENT_COLUMNS.length}: ${FITMENT_COLUMNS.join(', ')}.`
  );
}

/**
 * Turn the raw columnar payload into lookup maps.
 *
 * Fitment columns, in order: id, model, series, body_type, variant, qualifier,
 * year_from, year_to, year_label, collection, definitive, filters, note.
 *
 * @param {Object} doc parsed rb-vehicle-data document
 * @returns {Object} index consumed by every other function here
 * @throws {Error} when a fitment row is not FITMENT_COLUMNS long
 */
export function indexData(doc) {
  const source = doc && typeof doc === 'object' ? doc : {};

  const makes = toRows(source.makes).map((row) => ({
    handle: row[0],
    name: text(row[1]) ?? String(row[0]),
  }));

  const models = toRows(source.models).map((row) => ({
    make: row[0],
    handle: row[1],
    name: text(row[2]) ?? String(row[1]),
    collection: text(row[3]),
  }));

  const series = toRows(source.series).map((row) => ({
    model: row[0],
    handle: row[1],
    name: text(row[2]) ?? String(row[1]),
    code: text(row[3]),
    yearFrom: toYear(row[4]),
    yearTo: toYear(row[5]),
    helperText: text(row[6]),
    collection: text(row[7]),
  }));

  const fitmentRows = toRows(source.f);
  assertFitmentShape(fitmentRows);

  const fitments = fitmentRows.map((row) => ({
    id: row[0],
    model: row[1],
    series: row[2] ?? null,
    bodyType: text(row[3]),
    variant: text(row[4]),
    qualifier: text(row[5]),
    yearFrom: toYear(row[6]),
    yearTo: toYear(row[7]),
    yearLabel: text(row[8]),
    collection: text(row[9]),
    isDefinitive: row[10] === 1 || row[10] === true,
    filters: toRows(row[11]).map((filter) => ({
      id: filter[0],
      label: text(filter[1]) ?? String(filter[0]),
    })),
    note: text(row[12]),
  }));

  const makeByHandle = new Map(makes.map((make) => [make.handle, make]));
  const modelByHandle = new Map(models.map((model) => [model.handle, model]));
  const seriesByHandle = new Map(series.map((entry) => [entry.handle, entry]));
  const fitmentById = new Map(fitments.map((fitment) => [String(fitment.id), fitment]));

  // Case-folded twins, so a deep link that arrives with a differently cased make or
  // model handle finds the same record as the cascade does.
  const makeByFold = new Map(makes.map((make) => [fold(make.handle), make]));
  const modelByFold = new Map(models.map((model) => [fold(model.handle), model]));

  const modelsByMake = new Map();
  models.forEach((model) => {
    if (!modelsByMake.has(model.make)) modelsByMake.set(model.make, []);
    modelsByMake.get(model.make).push(model);
  });

  const seriesByModel = new Map();
  series.forEach((entry) => {
    if (!seriesByModel.has(entry.model)) seriesByModel.set(entry.model, []);
    seriesByModel.get(entry.model).push(entry);
  });

  const fitmentsByModel = new Map();
  fitments.forEach((fitment) => {
    if (!fitmentsByModel.has(fitment.model)) fitmentsByModel.set(fitment.model, []);
    fitmentsByModel.get(fitment.model).push(fitment);
  });

  // Body type is a closed vocabulary, so a raw export cell can be tested against it
  // before anything tries to split a qualifier out of it.
  const bodyTypeByFold = new Map();
  fitments.forEach((fitment) => {
    if (fitment.bodyType !== null) bodyTypeByFold.set(fold(fitment.bodyType), fitment.bodyType);
  });

  return {
    renderedAt: text(source.rendered_at ?? source.v),
    count: Number.isFinite(Number(source.count)) ? Number(source.count) : fitments.length,
    makes,
    makeByHandle,
    makeByFold,
    models,
    modelByHandle,
    modelByFold,
    modelsByMake,
    series,
    seriesByHandle,
    seriesByModel,
    fitments,
    fitmentById,
    fitmentsByModel,
    bodyTypeByFold,
  };
}

/* -------------------------------------------------------------------------- */
/* Variant option values                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Pack a fitment's body type, variant and qualifier into one <option value>.
 * @param {Object|null} fitment
 * @returns {string}
 */
export function variantValue(fitment) {
  const entry = fitment ?? {};
  return [entry.bodyType, entry.variant, entry.qualifier]
    .map((part) => part ?? '')
    .join(VARIANT_VALUE_SEPARATOR);
}

/**
 * Unpack an <option value> back into selection fields.
 * @param {string} value
 * @returns {{body: string|null, variant: string|null, qualifier: string|null}}
 */
export function parseVariantValue(value) {
  const parts = String(value ?? '').split(VARIANT_VALUE_SEPARATOR);
  return {
    body: text(parts[0]),
    variant: text(parts[1]),
    qualifier: text(parts[2]),
  };
}

/**
 * Pack a fitment's generation, body type, variant and qualifier into one <option value>
 * — the combined select that follows the year.
 *
 * @param {Object|null} fitment
 * @returns {string}
 */
export function trimValue(fitment) {
  const entry = fitment ?? {};
  return [entry.series, entry.bodyType, entry.variant, entry.qualifier]
    .map((part) => part ?? '')
    .join(VARIANT_VALUE_SEPARATOR);
}

/**
 * Unpack a trim <option value> back into selection fields. A blank part comes back as
 * null, which the resolver reads as "explicitly blank" rather than "unanswered", so a
 * fitment with no generation or no variant still matches exactly.
 *
 * @param {string} value
 * @returns {{series: string|null, body: string|null, variant: string|null, qualifier: string|null}}
 */
export function parseTrimValue(value) {
  const parts = String(value ?? '').split(VARIANT_VALUE_SEPARATOR);
  return {
    series: text(parts[0]),
    body: text(parts[1]),
    variant: text(parts[2]),
    qualifier: text(parts[3]),
  };
}

/* -------------------------------------------------------------------------- */
/* 2. candidates                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Does a fitment field satisfy a chosen value?
 * `undefined` means the shopper has not answered that level, so it never filters.
 * `null` means "explicitly blank" and only matches fitments that are blank there.
 *
 * @param {string|null} actual
 * @param {*} selected
 * @returns {boolean}
 */
function matchesLevel(actual, selected) {
  if (selected === undefined) return true;
  const wanted = selected === null ? null : fold(selected);
  return fold(actual) === wanted;
}

/**
 * Canonical make and model handles for a selection, so a deep link that arrives
 * with "Toyota" or "TOYOTA-HILUX" indexes the same records the cascade wrote.
 * An unknown handle is left as it came, so it still matches nothing.
 *
 * @param {Object|null} data index from indexData()
 * @param {Object|null} selection
 * @returns {Object} a copy, never the caller's object
 */
function normaliseSelection(data, selection) {
  const chosen = { ...(selection ?? {}) };
  if (!data || !(data.makeByFold instanceof Map) || !(data.modelByFold instanceof Map)) {
    return chosen;
  }
  if (chosen.make !== undefined && chosen.make !== null && !data.makeByHandle.has(chosen.make)) {
    chosen.make = data.makeByFold.get(fold(chosen.make))?.handle ?? chosen.make;
  }
  if (chosen.model !== undefined && chosen.model !== null && !data.modelByHandle.has(chosen.model)) {
    chosen.model = data.modelByFold.get(fold(chosen.model))?.handle ?? chosen.model;
  }
  return chosen;
}

/**
 * Fitments matching every chosen level, optionally narrowed to a known year.
 *
 * @param {Object} data index from indexData()
 * @param {Object|null} [selection] {make, model, series, body, variant, qualifier, fitmentId}
 * @param {number|string|null} [year] known build year, or null
 * @returns {Array<Object>} fitments in payload order
 */
export function candidates(data, selection, year = null) {
  if (!data || !Array.isArray(data.fitments)) return [];
  const chosen = normaliseSelection(data, selection);

  let pool;
  if (chosen.model) {
    pool = data.fitmentsByModel.get(chosen.model) ?? [];
  } else if (chosen.make) {
    pool = data.fitments.filter(
      (fitment) => data.modelByHandle.get(fitment.model)?.make === chosen.make
    );
  } else {
    pool = data.fitments;
  }

  const known = toYear(year);
  const wantedId =
    chosen.fitmentId === undefined || chosen.fitmentId === null ? null : String(chosen.fitmentId);

  return pool.filter((fitment) => {
    if (!matchesLevel(fitment.series, chosen.series)) return false;
    if (!matchesLevel(fitment.bodyType, chosen.body)) return false;
    if (!matchesLevel(fitment.variant, chosen.variant)) return false;
    if (!matchesLevel(fitment.qualifier, chosen.qualifier)) return false;
    if (wantedId !== null && String(fitment.id) !== wantedId) return false;
    if (known !== null && !coversYear(fitment, known)) return false;
    return true;
  });
}

/* -------------------------------------------------------------------------- */
/* 3. optionsFor                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Everything answered strictly above the given level.
 * @param {Object|null} selection
 * @param {string} level
 * @returns {Object}
 */
function selectionAbove(selection, level) {
  const chosen = selection ?? {};
  const above = {};
  if (chosen.make !== undefined) above.make = chosen.make;
  if (level === 'model') return above;
  if (chosen.model !== undefined) above.model = chosen.model;
  // The year and the combined select both sit directly under the model. The year is
  // not part of the selection object — it travels as its own argument — so the trim
  // is narrowed by it through candidates() rather than here.
  if (level === 'year' || level === 'trim' || level === 'series') return above;
  if (chosen.series !== undefined) above.series = chosen.series;
  if (level === 'variant') return above;
  if (chosen.body !== undefined) above.body = chosen.body;
  if (chosen.variant !== undefined) above.variant = chosen.variant;
  if (chosen.qualifier !== undefined) above.qualifier = chosen.qualifier;
  return above;
}

/**
 * Is this fitment's year range tighter than its series' range?
 * @param {Object} data
 * @param {Object} fitment
 * @returns {boolean}
 */
function isNarrowerThanSeries(data, fitment) {
  const entry = fitment.series ? data.seriesByHandle.get(fitment.series) : null;
  if (!entry) return false;
  const startsLater =
    entry.yearFrom !== null && fitment.yearFrom !== null && fitment.yearFrom > entry.yearFrom;
  const endsEarlier =
    fitment.yearTo !== null && (entry.yearTo === null || fitment.yearTo < entry.yearTo);
  return startsLater || endsEarlier;
}

/**
 * Generation label, e.g. "8th Gen (2015 – now)".
 * @param {Object} entry series
 * @returns {string}
 */
function seriesLabel(entry) {
  const range = yearRangeLabel(entry.yearFrom, entry.yearTo);
  return range === '' ? entry.name : `${entry.name} (${range})`;
}

/**
 * Build the variant groups for a candidate set.
 * @param {Object} data
 * @param {Array<Object>} cands
 * @param {{withYearBands?: boolean, valueOf?: Function}} [settings]
 *   `withYearBands` appends a fitment's own range to its label when it is narrower than
 *   its generation — useful when the year is unknown, noise once it has been asked.
 *   `valueOf` packs each option's value; the trim level includes the generation.
 * @returns {{groups: Array<Object>, options: Array<Object>, isBodyOnly: boolean}}
 */
function variantGroups(data, cands, settings = null) {
  const withYearBands = settings?.withYearBands !== false;
  const valueOf = typeof settings?.valueOf === 'function' ? settings.valueOf : variantValue;
  const list = toList(cands);
  const hasAnyVariant = list.some((fitment) => fitment.variant !== null);

  // Nothing to ask: no candidate carries a body type, a variant or a qualifier, so
  // the field is removed rather than filled with meaningless "Standard" options.
  const isFeatureless = list.every(
    (fitment) =>
      fitment.bodyType === null && fitment.variant === null && fitment.qualifier === null
  );
  if (isFeatureless) {
    return { groups: [], options: [], isBodyOnly: list.length > 0 && !hasAnyVariant };
  }

  const groupIndex = new Map();
  const groups = [];
  const optionIndex = new Map();

  list.forEach((fitment) => {
    const parts = hasAnyVariant
      ? [fitment.variant, fitment.qualifier]
      : [fitment.bodyType, fitment.qualifier];
    // A fitment whose variant and qualifier are both blank still needs a label, or
    // it would drop out of the field and become unreachable through the cascade.
    const label =
      parts.map((part) => text(part)).filter(Boolean).join(` ${MIDDLE_DOT} `)
      || text(fitment.bodyType)
      || STANDARD_LABEL;

    // Body type doubles as the field's own options when nothing carries a variant,
    // so it must not also become an <optgroup> heading.
    const groupBody = hasAnyVariant ? fitment.bodyType : null;
    const groupKey = groupBody ?? '';
    if (!groupIndex.has(groupKey)) {
      const group = { body: groupBody, options: [] };
      groupIndex.set(groupKey, group);
      groups.push(group);
    }

    const value = valueOf(fitment);
    const optionKey = `${groupKey}${VARIANT_VALUE_SEPARATOR}${value}`;
    if (optionIndex.has(optionKey)) {
      optionIndex.get(optionKey).fitments.push(fitment);
      return;
    }

    const option = {
      value,
      label,
      series: fitment.series ?? null,
      body: fitment.bodyType,
      variant: fitment.variant,
      qualifier: fitment.qualifier,
      fitments: [fitment],
    };
    optionIndex.set(optionKey, option);
    groupIndex.get(groupKey).options.push(option);
  });

  // A year band only helps when the option is a single fitment; when several share
  // the label the Years step separates them instead.
  const options = [];
  groups.forEach((group) => {
    group.options.forEach((option) => {
      const [only] = option.fitments;
      if (withYearBands && option.fitments.length === 1 && isNarrowerThanSeries(data, only)) {
        option.label = `${option.label} (${yearRangeLabel(only.yearFrom, only.yearTo)})`;
      }
      delete option.fitments;
      options.push(option);
    });
  });

  return { groups, options, isBodyOnly: list.length > 0 && !hasAnyVariant };
}

/**
 * Newest generation first, generation-less fitments last. The Year select is newest
 * first too, so a changeover year lists the generation the shopper most likely has on top.
 *
 * @param {Object} data
 * @param {Array<string|null>} handles
 * @returns {Array<string|null>}
 */
function newestSeriesFirst(data, handles) {
  const startOf = (handle) => (handle ? data.seriesByHandle.get(handle)?.yearFrom ?? null : null);
  return [...handles].sort((left, right) => {
    const a = startOf(left);
    const b = startOf(right);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return b - a;
  });
}

/**
 * Options for the combined generation / variant select, for a candidate set that the
 * build year has already narrowed.
 *
 * In most model-years the year pins the generation, so the field is exactly the old
 * Variant field — grouped by body type — and the generation is never asked about. In a
 * changeover year, or where two generations were sold side by side, it is grouped by
 * generation instead, with the body folded into each label because a native <select>
 * cannot nest <optgroup>s.
 *
 * @param {Object} data
 * @param {Array<Object>} cands
 * @returns {{groups: Array<Object>, options: Array<Object>, isBodyOnly: boolean}}
 */
function trimGroups(data, cands) {
  const list = toList(cands);
  if (list.length === 0) return { groups: [], options: [], isBodyOnly: false };

  const generations = [];
  list.forEach((fitment) => {
    const handle = fitment.series ?? null;
    if (!generations.includes(handle)) generations.push(handle);
  });

  const isFeatureless = list.every(
    (fitment) =>
      fitment.bodyType === null && fitment.variant === null && fitment.qualifier === null
  );

  // One generation: nothing to say about it, so the field is the familiar Variant field.
  if (generations.length === 1) {
    const built = variantGroups(data, list, { withYearBands: false, valueOf: trimValue });
    const groups = built.groups.map((group) => ({ label: group.body, body: group.body, options: group.options }));
    // A field with nothing in it is removed, so it has no name to rename to "Body type".
    return { groups, options: built.options, isBodyOnly: built.options.length > 0 && built.isBodyOnly };
  }

  const hasAnyVariant = list.some((fitment) => fitment.variant !== null);
  const options = [];
  const groups = newestSeriesFirst(data, generations).map((handle) => {
    const entry = handle ? data.seriesByHandle.get(handle) ?? null : null;
    const seen = new Set();
    const groupOptions = [];

    list
      .filter((fitment) => (fitment.series ?? null) === handle)
      .forEach((fitment) => {
        const value = trimValue(fitment);
        if (seen.has(value)) return;
        seen.add(value);

        // Two generations with nothing else to tell them apart: the generation is the
        // whole answer, so each group carries a single option naming it.
        const label = isFeatureless
          ? entry?.name ?? STANDARD_LABEL
          : [fitment.bodyType, fitment.variant, fitment.qualifier]
            .map((part) => text(part))
            .filter(Boolean)
            .join(` ${MIDDLE_DOT} `) || STANDARD_LABEL;

        const option = {
          value,
          label,
          series: handle,
          body: fitment.bodyType,
          variant: fitment.variant,
          qualifier: fitment.qualifier,
        };
        groupOptions.push(option);
        options.push(option);
      });

    return { label: entry ? seriesLabel(entry) : STANDARD_LABEL, series: handle, options: groupOptions };
  });

  return { groups, options, isBodyOnly: !isFeatureless && !hasAnyVariant };
}

/**
 * Has the combined generation / variant field been answered?
 *
 * The component always writes all four of its parts, so any body, variant or qualifier
 * key means an answer. A generation on its own — a deep link naming only a series —
 * answers it only when nothing below the generation varies; otherwise it narrows the
 * list and the shopper still picks from it.
 *
 * @param {Object} data
 * @param {Object} chosen
 * @param {number|string|null} year
 * @returns {boolean}
 */
function isTrimAnswered(data, chosen, year) {
  if (isVariantAnswered(chosen)) return true;
  if (chosen.series === undefined) return false;
  return candidates(data, chosen, year).every(
    (fitment) =>
      fitment.bodyType === null && fitment.variant === null && fitment.qualifier === null
  );
}

/**
 * Distinct, ordered options for one field of the cascade.
 *
 * Returns `{ level, options, groups, isBodyOnly, isRemoved, isAutoSelect }`:
 *  - `options` is the flat list (for 'variant' and 'trim' it is the groups flattened);
 *  - `groups` is `[{ label, options }]` for 'trim', `[{ body, options }]` for
 *    'variant', and null elsewhere;
 *  - `isBodyOnly` is true when no candidate carries a variant, so the caller
 *    renders the field as "Body type";
 *  - `isRemoved` means zero options, so the field leaves the DOM entirely;
 *  - `isAutoSelect` means exactly one option, so the caller selects it.
 *
 * The cascade asks 'year', 'trim' and 'band'. 'series' and 'variant' are the building
 * blocks 'trim' is composed from and remain callable on their own; 'years' is the old
 * name for 'band'.
 *
 * @param {'model'|'year'|'trim'|'band'|'series'|'variant'|'years'} level
 * @param {Object} data index from indexData()
 * @param {Object|null} [selection]
 * @param {number|string|null} [year] known build year, or null
 * @param {number} [now] epoch milliseconds, for the top of an open-ended year range
 * @returns {Object}
 */
export function optionsFor(level, data, selection, year = null, now = Date.now()) {
  const result = {
    level,
    options: [],
    groups: null,
    isBodyOnly: false,
    isRemoved: true,
    isAutoSelect: false,
  };
  if (!data) return result;

  const chosen = normaliseSelection(data, selection);
  // The year list is what the shopper is choosing from, so it is never narrowed by a
  // year; every level below the year is.
  const cands = candidates(data, selectionAbove(chosen, level), level === 'year' ? null : year);

  if (level === 'year') {
    // Every year any of the model's fitments covers, newest first. A range with no end
    // runs to this year; a gap no fitment covers simply has no option.
    const thisYear = currentYear(now);
    const covered = new Set();
    cands.forEach((fitment) => {
      if (fitment.yearFrom === null) return;
      const last = fitment.yearTo ?? Math.max(thisYear, fitment.yearFrom);
      for (let value = fitment.yearFrom; value <= last; value += 1) covered.add(value);
    });
    result.options = [...covered]
      .sort((left, right) => right - left)
      .map((value) => ({ value: String(value), label: String(value) }));
  } else if (level === 'trim') {
    const built = trimGroups(data, cands);
    result.groups = built.groups;
    result.options = built.options;
    result.isBodyOnly = built.isBodyOnly;
  } else if (level === 'model') {
    const present = new Set(cands.map((fitment) => fitment.model));
    const pool = chosen.make ? data.modelsByMake.get(chosen.make) ?? [] : data.models;
    result.options = pool
      .filter((model) => present.has(model.handle))
      .map((model) => ({ value: model.handle, label: model.name, collection: model.collection }));
  } else if (level === 'series') {
    const present = new Set(cands.map((fitment) => fitment.series).filter(Boolean));
    const pool = chosen.model ? data.seriesByModel.get(chosen.model) ?? [] : data.series;
    result.options = pool
      .filter((entry) => present.has(entry.handle))
      .map((entry) => ({
        value: entry.handle,
        label: seriesLabel(entry),
        name: entry.name,
        code: entry.code,
        helperText: entry.helperText,
        collection: entry.collection,
      }));
  } else if (level === 'variant') {
    const built = variantGroups(data, cands);
    result.groups = built.groups;
    result.options = built.options;
    result.isBodyOnly = built.isBodyOnly;
  } else if (level === 'band' || level === 'years') {
    // One survivor needs no question; the caller resolves it straight away.
    result.options =
      cands.length > 1
        ? cands.map((fitment) => ({
            value: String(fitment.id),
            label: fitment.yearLabel ?? yearRangeLabel(fitment.yearFrom, fitment.yearTo),
            note: fitment.note,
            fitmentId: fitment.id,
          }))
        : [];
  }

  if (result.groups !== null) {
    result.groups = result.groups.filter((group) => group.options.length > 0);
  }
  result.isRemoved = result.options.length === 0;
  result.isAutoSelect = result.options.length === 1;
  return result;
}

/* -------------------------------------------------------------------------- */
/* 4. resolveDestination                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Identity of a fitment's destination: collection plus the filter it applies.
 * @param {Object|null} fitment
 * @returns {string}
 */
function destinationKey(fitment) {
  const entry = fitment ?? {};
  const filterIds = toList(entry.filters)
    .map((filter) => String(filter?.id ?? ''))
    .sort();
  return `${entry.collection ?? ''}\u0000${filterIds.join(',')}`;
}

/**
 * Build the theme's own filter query string from filter labels.
 * The search store writes and reads `filter-<id>=Label1,Label2` through
 * URLSearchParams, so this uses the same encoder and round-trips exactly.
 *
 * @param {Array<{id: *, label: string}>} filters
 * @returns {string} e.g. "filter-filter.p.m.custom.vehicle_variants=Premium%2CDynamic"
 */
export function buildFilterQuery(filters) {
  const labels = toList(filters).map((filter) => text(filter?.label)).filter(Boolean);
  if (labels.length === 0) return '';
  const params = new URLSearchParams();
  params.set(`filter-${FILTER_KEY}`, labels.join(','));
  return params.toString();
}

/**
 * The destination every candidate shares, or null when they disagree.
 *
 * @param {Array<Object>|null} cands
 * @returns {{collection: string, filters: Array, query: string, definitive: boolean}|null}
 */
export function resolveDestination(cands) {
  const list = toList(cands);
  if (list.length === 0) return null;

  const [first] = list;
  const collection = text(first.collection);
  if (collection === null) return null;

  const key = destinationKey(first);
  if (list.some((fitment) => destinationKey(fitment) !== key)) return null;

  const filters = toList(first.filters).map((filter) => ({ id: filter.id, label: filter.label }));
  return {
    collection,
    filters,
    query: buildFilterQuery(filters),
    definitive: first.isDefinitive === true || filters.length > 0,
  };
}

/**
 * Destination as a storefront path, e.g. "/collections/kia-ev5-seat-covers".
 * @param {Object|null} dest
 * @returns {string|null}
 */
export function destinationUrl(dest) {
  if (!dest || !dest.collection) return null;
  return `/collections/${dest.collection}${dest.query ? `?${dest.query}` : ''}`;
}

/* -------------------------------------------------------------------------- */
/* 5. nextStep                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Has the shopper answered the variant field in any form?
 * @param {Object} selection
 * @returns {boolean}
 */
function isVariantAnswered(selection) {
  return (
    selection.variant !== undefined ||
    selection.body !== undefined ||
    selection.qualifier !== undefined
  );
}

/**
 * The first level the shopper has not answered that actually exists for this model.
 *
 * Returns a member of LEVELS while something is left to ask, IMPOSSIBLE when the
 * selection matches no fitment at all, and null once one destination is settled.
 * Callers must tell those last two apart: an impossible selection has no vehicle
 * to save, while a settled one has.
 *
 * The order is Make → Model → Year → generation / variant → build date: customers know
 * the year of their vehicle far more readily than its generation, and once the year is
 * known the generation is usually settled without asking.
 *
 * @param {Object} data index from indexData()
 * @param {Object|null} [selection]
 * @param {number|string|null} [year] known build year, or null
 * @returns {'make'|'model'|'year'|'trim'|'band'|'impossible'|null}
 */
export function nextStep(data, selection, year = null) {
  if (!data) return null;
  const chosen = normaliseSelection(data, selection);
  if (!chosen.make && !chosen.model) return 'make';
  if (candidates(data, chosen, year).length === 0) return IMPOSSIBLE;
  if (!chosen.model) return 'model';

  if (toYear(year) === null && !optionsFor('year', data, chosen, null).isRemoved) {
    return 'year';
  }
  if (!isTrimAnswered(data, chosen, year) && !optionsFor('trim', data, chosen, year).isRemoved) {
    return 'trim';
  }
  const hasFitmentId = chosen.fitmentId !== undefined && chosen.fitmentId !== null;
  if (!hasFitmentId && !optionsFor('band', data, chosen, year).isRemoved) {
    return 'band';
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* 6. buildVehicle                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Every destination handle that belongs to a model: its fitments' collections, its
 * series' fallback collections and the model's own "shop all" collection.
 * Captured at save time so the PLP bar and PDP card never need the catalogue.
 *
 * @param {Object} data
 * @param {Object} model
 * @returns {Array<string>}
 */
function modelCollectionsFor(data, model) {
  const handles = [];
  (data.fitmentsByModel.get(model.handle) ?? []).forEach((fitment) =>
    pushUnique(handles, fitment.collection)
  );
  (data.seriesByModel.get(model.handle) ?? []).forEach((entry) =>
    pushUnique(handles, entry.collection)
  );
  pushUnique(handles, model.collection);
  return handles;
}

/**
 * The destination used when the candidates do not agree: the chosen series'
 * collection, else the model's collection.
 * @param {Object|null} seriesEntry
 * @param {Object} model
 * @returns {Object}
 */
function fallbackDestination(seriesEntry, model) {
  return {
    collection: seriesEntry?.collection ?? model.collection ?? null,
    filters: [],
    query: '',
    definitive: false,
  };
}

/**
 * The saved-vehicle object persisted as `rb_vehicle`.
 *
 * `complete` is false when the shopper submitted before one fitment remained;
 * `candidates` then lists every fitment still possible and `dest` is the
 * destination they share, or the series or model fallback collection.
 *
 * Returns null when the selection matches no fitment at all, so an impossible
 * vehicle is never invented and then treated as real by fitmentState().
 *
 * @param {Object} data index from indexData()
 * @param {Object|null} selection
 * @param {number|string|null} [year] known build year, or null
 * @param {Array<Object>|null} [cands] candidate list, recomputed when omitted
 * @param {number} [now] epoch milliseconds, injectable so the function stays pure
 * @returns {Object|null}
 */
export function buildVehicle(data, selection, year = null, cands = null, now = Date.now()) {
  const chosen = normaliseSelection(data, selection);
  if (!data || !chosen.model) return null;
  const model = data.modelByHandle.get(chosen.model);
  if (!model) return null;

  const make = data.makeByHandle.get(chosen.make ?? model.make) ?? {
    handle: model.make,
    name: model.make,
  };
  const list = Array.isArray(cands) ? toList(cands) : candidates(data, chosen, year);
  if (list.length === 0) return null;

  const isComplete = list.length === 1;
  const only = isComplete ? list[0] : null;

  const seriesHandle = only ? only.series : chosen.series ?? null;
  const seriesEntry = seriesHandle ? data.seriesByHandle.get(seriesHandle) ?? null : null;
  const dest = resolveDestination(list) ?? fallbackDestination(seriesEntry, model);
  const step = isComplete ? null : nextStep(data, chosen, year);

  return {
    v: 1,
    id: only ? only.id : null,
    complete: isComplete,
    // The build year the shopper chose, which seeds the Year select next time. Null when
    // they submitted before answering it — the CTA goes live as soon as the destination
    // is settled — and on every vehicle saved before the year-first cascade.
    year: toYear(year),
    make: { h: make.handle, n: make.name },
    model: { h: model.handle, n: model.name, c: model.collection },
    // `c` is the generation's own collection, so a surface that lists generations
    // (rb-gen-band's model mode) can mark the shopper's one without the catalogue.
    series: seriesEntry
      ? { h: seriesEntry.handle, n: seriesEntry.name, code: seriesEntry.code, c: seriesEntry.collection }
      : null,
    body: only ? only.bodyType : text(chosen.body),
    variant: only ? only.variant : text(chosen.variant),
    qualifier: only ? only.qualifier : text(chosen.qualifier),
    years: only ? { from: only.yearFrom, to: only.yearTo, label: only.yearLabel } : null,
    dest: {
      collection: dest.collection,
      query: dest.query,
      filterIds: dest.filters.map((filter) => filter.id),
      definitive: dest.definitive,
    },
    // The field the shopper still has to answer, decided against the catalogue at
    // save time so the copy strings never ask for a field this model does not have.
    next: NEXT_STEP_WORDS[step] ?? '',
    candidates: list.map((fitment) => fitment.id),
    modelCollections: modelCollectionsFor(data, model),
    savedAt: Math.floor(now / 1000),
  };
}

/* -------------------------------------------------------------------------- */
/* 7. labels and fill                                                          */
/* -------------------------------------------------------------------------- */

const EMPTY_LABELS = {
  year: '',
  make: '',
  model: '',
  series: '',
  variant: '',
  model_series: '',
  series_variant: '',
  model_series_variant: '',
  sentence: '',
  next_step: '',
};

/**
 * The level a vehicle saved before `next` existed still has to answer.
 * Kept only as the fallback for those older saves.
 *
 * @param {Object} vehicle
 * @returns {string}
 */
function legacyNextStepWord(vehicle) {
  if (vehicle.complete === true) return '';
  if (!vehicle.series) return 'generation';
  if (!vehicle.variant && !vehicle.body) return 'variant';
  return 'year';
}

/**
 * Placeholder map for every vehicle-aware copy string.
 * @param {Object|null} vehicle saved vehicle from buildVehicle()
 * @returns {Object<string, string>}
 */
export function labels(vehicle) {
  const saved = vehicle ?? null;
  if (!saved) return { ...EMPTY_LABELS };

  const make = text(saved.make?.n) ?? '';
  const model = text(saved.model?.n) ?? '';
  const series = text(saved.series?.n) ?? '';
  const variant = text(saved.variant) ?? text(saved.body) ?? '';
  const year = toYear(saved.year);

  return {
    year: year === null ? '' : String(year),
    make,
    model,
    series,
    variant,
    model_series: joinWords([model, series]),
    series_variant: joinWords([series, variant]),
    // The PLP bar's "HiLux 8th Gen · SR5": the variant set apart the way the chip sets it.
    model_series_variant: [joinWords([model, series]), variant].filter(Boolean).join(' · '),
    sentence: joinWords([make, model, series, variant]),
    next_step: typeof saved.next === 'string' ? saved.next : legacyNextStepWord(saved),
  };
}

/**
 * Replace every `{token}` in a template with the vehicle's label.
 * Tokens are matched case-insensitively, so a merchant who types `{Model}` into a
 * setting still gets the model rather than literal braces. Unknown tokens are left
 * alone so a typo is visible rather than silently blank.
 *
 * @param {string} template
 * @param {Object|null} vehicle
 * @returns {string}
 */
export function fill(template, vehicle) {
  if (typeof template !== 'string' || template === '') return '';
  const map = labels(vehicle);
  return template.replace(/\{([A-Za-z_]+)\}/g, (match, token) => {
    const key = token.toLowerCase();
    return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : match;
  });
}

/* -------------------------------------------------------------------------- */
/* 8. fitmentState                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Does the product carry at least one of the destination's filter values?
 * A destination with no filter is satisfied by definition.
 *
 * @param {Array} filterIds
 * @param {Array} variantIds
 * @returns {boolean}
 */
function isFilterSatisfied(filterIds, variantIds) {
  const wanted = toList(filterIds).map((id) => String(id));
  if (wanted.length === 0) return true;
  const held = new Set(toList(variantIds).map((id) => String(id)));
  return wanted.some((id) => held.has(id));
}

/**
 * Which PDP fitment card state to render.
 *
 * Evaluated in the order of the solution doc's table:
 *  hidden → prompt → fits → confirm → other_variant → no_fit.
 *
 * @param {{vehicleCollections: Array<{h: string, definitive: boolean}>,
 *          variantIds: Array<number>, isExempt: boolean}} product
 * @param {Object|null} vehicle saved vehicle
 * @returns {'hidden'|'prompt'|'fits'|'confirm'|'other_variant'|'no_fit'}
 */
export function fitmentState(product, vehicle) {
  const collections = toList(product?.vehicleCollections).filter((entry) => entry && entry.h);

  if (product?.isExempt === true || collections.length === 0) return 'hidden';
  if (!vehicle || !vehicle.dest) return 'prompt';

  const destination = text(vehicle.dest.collection);
  const match = destination
    ? collections.find((entry) => entry.h === destination) ?? null
    : null;

  if (match && isFilterSatisfied(vehicle.dest.filterIds, product?.variantIds)) {
    const isDefinitive = vehicle.dest.definitive === true || match.definitive === true;
    return vehicle.complete === true && isDefinitive ? 'fits' : 'confirm';
  }

  const modelSet = new Set(toList(vehicle.modelCollections).filter(Boolean));
  if (collections.some((entry) => modelSet.has(entry.h))) return 'other_variant';

  return 'no_fit';
}

/* -------------------------------------------------------------------------- */
/* 9. refreshVehicle                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Re-resolve a saved vehicle against a freshly loaded catalogue.
 *
 * A vehicle is saved once and read on every later page view, so the catalogue
 * underneath it can change: a fitment can be deleted, a destination collection can
 * be renamed, a model can gain a generation. Without this the PDP would keep
 * showing a confident green "Fits your …" for a fitment that no longer exists.
 *
 * Returns `{ vehicle, isStale }`:
 *  - `vehicle` is rebuilt from the live catalogue, with fresh dest, next-step word
 *    and modelCollections, and keeps the original savedAt;
 *  - `vehicle` is null when nothing it named survives, so the caller clears the
 *    store and asks again;
 *  - `isStale` is true whenever any fitment it named has gone, so the caller can
 *    downgrade a "fits" to a "confirm" and prompt the shopper to check.
 *
 * @param {Object} data index from indexData()
 * @param {Object|null} vehicle saved vehicle from buildVehicle()
 * @param {number} [now] epoch milliseconds, used only when the save carried no time
 * @returns {{vehicle: Object|null, isStale: boolean}}
 */
export function refreshVehicle(data, vehicle, now = Date.now()) {
  const saved = vehicle ?? null;
  if (!saved) return { vehicle: null, isStale: false };
  if (!data || !Array.isArray(data.fitments)) return { vehicle: saved, isStale: false };

  const modelHandle = saved.model?.h ?? null;
  const model = modelHandle
    ? data.modelByHandle.get(modelHandle) ?? data.modelByFold.get(fold(modelHandle)) ?? null
    : null;
  if (!model) return { vehicle: null, isStale: true };

  const savedIds = saved.id === null || saved.id === undefined
    ? toList(saved.candidates)
    : [saved.id];
  const live = savedIds.map((id) => data.fitmentById.get(String(id))).filter(Boolean);
  const isStale = live.length !== savedIds.length;
  if (savedIds.length > 0 && live.length === 0) return { vehicle: null, isStale: true };

  const selection = { make: saved.make?.h, model: model.handle };
  if (saved.series?.h) selection.series = saved.series.h;
  if (saved.body !== null && saved.body !== undefined) selection.body = saved.body;
  if (saved.variant !== null && saved.variant !== undefined) selection.variant = saved.variant;
  if (saved.qualifier !== null && saved.qualifier !== undefined) {
    selection.qualifier = saved.qualifier;
  }

  const savedAtMs = Number.isFinite(Number(saved.savedAt)) ? Number(saved.savedAt) * 1000 : now;
  // The build year is carried through so the rebuilt vehicle keeps it, and so the
  // next-step word is judged against the same year the shopper answered.
  const rebuilt = buildVehicle(
    data,
    selection,
    toYear(saved.year),
    live.length > 0 ? live : null,
    savedAtMs
  );
  if (!rebuilt) return { vehicle: null, isStale: true };
  if (rebuilt.complete === true && !wasComplete(saved)) {
    return { vehicle: keepPartial(rebuilt, saved), isStale: true };
  }
  return { vehicle: rebuilt, isStale };
}

/**
 * Did the shopper answer every question this vehicle had when they saved it?
 *
 * `complete` has been on the saved object since v1, so it is the field to trust;
 * `next` — the word for the question they left unanswered — corroborates it for any
 * save that somehow carries one and not the other. Both are persisted, which is what
 * makes this a stable reading on a later page view.
 *
 * @param {Object} saved
 * @returns {boolean}
 */
function wasComplete(saved) {
  if (saved.complete === true) return true;
  if (saved.complete === false) return false;
  return text(saved.next) === null;
}

/**
 * Hold a partial saved vehicle to what the shopper actually told us.
 *
 * When the content team deletes fitments, the ones a partial vehicle named can thin
 * out to a single survivor — and buildVehicle, which has no way to tell "the only
 * fitment left" from "the fitment they chose", would read that survivor's generation,
 * variant, qualifier and year band straight onto the vehicle. The shopper who saved
 * "BYD Sealion 6" would come back to "BYD Sealion 6 Premium" and a green Fits badge
 * for a variant they were never shown.
 *
 * Everything the catalogue is genuinely the authority on is kept fresh: the
 * destination, the surviving candidate list and the model collections. Only the
 * answers are held back, and the destination stops claiming to be definitive so the
 * PDP asks the shopper to confirm.
 *
 * @param {Object} rebuilt the vehicle buildVehicle() returned
 * @param {Object} saved the vehicle as the shopper left it
 * @returns {Object}
 */
function keepPartial(rebuilt, saved) {
  // Blank in the save means unanswered, so it stays blank; answered means the fresh
  // value, which is the same answer re-resolved against the live catalogue.
  const answered = (savedValue, freshValue) => (text(savedValue) === null ? null : freshValue);

  return {
    ...rebuilt,
    id: null,
    complete: false,
    series: saved.series?.h ? rebuilt.series : null,
    body: answered(saved.body, rebuilt.body),
    variant: answered(saved.variant, rebuilt.variant),
    qualifier: answered(saved.qualifier, rebuilt.qualifier),
    years: null,
    dest: { ...rebuilt.dest, definitive: false },
    next: text(saved.next) === null ? legacyNextStepWord(saved) : saved.next,
  };
}

/* -------------------------------------------------------------------------- */
/* 10. resolveForRow                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Match a raw export value against a list of entries by name, tolerating case and
 * punctuation differences only. Genuine renames are the importer's display-name map.
 *
 * @param {Array<Object>} entries
 * @param {Array<string>} keys fields on each entry to compare
 * @param {*} raw
 * @returns {Object|null}
 */
function matchByName(entries, keys, raw) {
  const wantedFold = fold(raw);
  if (wantedFold === null) return null;
  const wantedSlug = slugFold(raw);

  const exact = toList(entries).find((entry) => keys.some((key) => fold(entry[key]) === wantedFold));
  if (exact) return exact;

  return (
    toList(entries).find((entry) => keys.some((key) => slugFold(entry[key]) === wantedSlug)) ?? null
  );
}

/**
 * Candidate spellings for a raw "Gen/Series" cell: the cell itself, the cell without
 * its parenthetical, and each side of a "CODE / NAME" pair.
 *
 * @param {string} raw
 * @returns {Array<string>}
 */
function seriesTokens(raw) {
  const cleaned = String(raw).replace(/"/g, ' ');
  const withoutBrackets = cleaned.replace(/\([^)]*\)/g, ' ');
  const tokens = [cleaned, withoutBrackets];
  withoutBrackets.split('/').forEach((part) => tokens.push(part));
  return tokens.map((token) => text(token)).filter(Boolean);
}

/**
 * Split a combined export label such as "SR5 - Cloth Seats" or "Ti-L (with headrest
 * screen)" into its own value plus the seat or trim qualifier.
 *
 * @param {*} raw
 * @returns {{value: string|null, qualifier: string|null}}
 */
function splitQualifier(raw) {
  const cleaned = text(raw);
  if (cleaned === null) return { value: null, qualifier: null };

  const bracketed = cleaned.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  if (bracketed) {
    return { value: text(bracketed[1]), qualifier: text(bracketed[2]) };
  }

  const separated = cleaned.split(/\s+-\s+/);
  if (separated.length > 1) {
    return { value: text(separated[0]), qualifier: text(separated.slice(1).join(' - ')) };
  }

  return { value: cleaned, qualifier: null };
}

/**
 * Read the raw "Body Type" cell.
 *
 * Body type is a closed vocabulary, so the whole cell is tried against it first:
 * "DUAL CAB (V-Active)" is a body type in its own right and must not be split into
 * "DUAL CAB" plus a "V-Active" qualifier. A cell the vocabulary does not know is then
 * tried against BODY_TYPE_ALIASES, the same table the importer folds by, so
 * "DUAL/DOUBLE CAB" lands on "Dual Cab". Only a cell neither knows falls through to
 * the qualifier split, which is what "DUAL CAB - Leather Seats" needs.
 *
 * @param {Object} data index from indexData()
 * @param {*} raw
 * @returns {{value: string|null, qualifier: string|null}}
 */
function readBodyCell(data, raw) {
  const cleaned = text(raw);
  if (cleaned === null) return { value: null, qualifier: null };
  const known = data.bodyTypeByFold?.get(fold(cleaned));
  if (known) return { value: known, qualifier: null };
  const aliased = BODY_ALIAS_BY_FOLD.get(fold(cleaned));
  if (aliased) return { value: aliased, qualifier: null };

  // An alias can also carry a seat or trim qualifier, so the split half is
  // folded through the same table rather than left as the raw spelling.
  const split = splitQualifier(cleaned);
  const aliasedValue = split.value === null ? null : BODY_ALIAS_BY_FOLD.get(fold(split.value));
  return aliasedValue ? { value: aliasedValue, qualifier: split.qualifier } : split;
}

/**
 * Turn one raw export row into a resolver selection.
 * @param {Object} data
 * @param {Object} row
 * @returns {{selection: Object, year: number|null}|null}
 */
function selectionFromRow(data, row) {
  if (!data || !row) return null;

  const make = matchByName(data.makes, ['name', 'handle'], row.Make);
  if (!make) return null;

  const modelPool = data.modelsByMake.get(make.handle) ?? [];
  const model = matchByName(modelPool, ['name', 'handle'], row.Model);
  if (!model) return null;

  const selection = { make: make.handle, model: model.handle };

  const rawSeries = text(row['Gen/Series']);
  if (rawSeries !== null) {
    const seriesPool = data.seriesByModel.get(model.handle) ?? [];
    let matched = null;
    seriesTokens(rawSeries).some((token) => {
      matched = matchByName(seriesPool, ['name', 'code', 'handle'], token);
      return matched !== null;
    });
    // An unmatched series is left unconstrained: the candidates then straddle more
    // than one destination and the row resolves to null rather than to a wrong URL.
    if (matched) selection.series = matched.handle;
  }

  const body = readBodyCell(data, row['Body Type']);
  const variant = splitQualifier(row.Variant);
  if (body.value !== null) selection.body = body.value;
  if (variant.value !== null) selection.variant = variant.value;

  const qualifier = variant.qualifier ?? body.qualifier;
  if (qualifier !== null) selection.qualifier = qualifier;

  return { selection, year: toYear(row.Year) };
}

/**
 * The destination path one raw export row resolves to, so the migration's verify
 * step and the storefront can never disagree. Returns null when the row does not
 * land on exactly one destination.
 *
 * @param {Object} data index from indexData()
 * @param {Object} row {Make, Model, Year, 'Gen/Series', 'Body Type', Variant}
 * @returns {string|null} e.g. "/collections/toyota-hilux-8th-gen-seat-covers"
 */
export function resolveForRow(data, row) {
  const parsed = selectionFromRow(data, row);
  if (!parsed) return null;
  return destinationUrl(resolveDestination(candidates(data, parsed.selection, parsed.year)));
}

export default {
  indexData,
  candidates,
  optionsFor,
  resolveDestination,
  destinationUrl,
  buildFilterQuery,
  nextStep,
  buildVehicle,
  refreshVehicle,
  labels,
  fill,
  fitmentState,
  resolveForRow,
  variantValue,
  parseVariantValue,
  trimValue,
  parseTrimValue,
  yearRangeLabel,
  coversYear,
  FILTER_KEY,
  VARIANT_VALUE_SEPARATOR,
  BODY_TYPE_ALIASES,
  LEVELS,
  IMPOSSIBLE,
};
